// SPDX-License-Identifier: GPL-3.0-or-later
// Small helpers that the SigmaBot part files share (own code, not ported).
// One part is one mesh with one material, put in a parent at a body-unit position.
// Body units: up is +y, the front of the bot is +z, the origin is the foot anchor.

import type { BufferGeometry, Mesh, Object3D } from "three";
import { put, solid } from "../common.ts";
import { hexOf, type Rgba } from "./kit.ts";

/** Optional scale and turn of a part. */
export interface PartOpts {
  readonly sx?: number;
  readonly sy?: number;
  readonly sz?: number;
  readonly rx?: number;
  readonly ry?: number;
  readonly rz?: number;
  readonly name?: string;
}

/** Add one part to a parent. */
export function part(
  parent: Object3D,
  geometry: BufferGeometry,
  color: Rgba,
  x = 0,
  y = 0,
  z = 0,
  o: PartOpts = {},
): Mesh {
  const mesh = put(geometry, solid(hexOf(color)), x, y, z);
  partPose(mesh, o);
  if (o.name) mesh.name = o.name;
  parent.add(mesh);
  return mesh;
}

/** Set the scale and the turn of a part. A value that is not set keeps its default. */
function partPose(mesh: Mesh, o: PartOpts): void {
  mesh.scale.set(o.sx ?? 1, o.sy ?? 1, o.sz ?? 1);
  mesh.rotation.set(o.rx ?? 0, o.ry ?? 0, o.rz ?? 0);
}
